/**
 * UI Controller - Preact Orchestrator
 * 
 * Thin wrapper that manages Preact rendering and state for the visualizer UI.
 * All DOM manipulation is now handled by Preact components.
 */

import { render } from 'preact';
import { MaterialsPanel } from './components/panels/MaterialsPanel';
import { ViewPresetsDropdown } from './components/panels/ViewPresetsDropdown';
import { BackgroundPanel } from './components/panels/BackgroundPanel';
import { ControlsBar } from './components/panels/ControlsBar';
import { KeybindsPanel } from './components/panels/KeybindsPanel';
import { LoadingOverlay } from './components/overlays/LoadingOverlay';
import { ErrorOverlay } from './components/overlays/ErrorOverlay';
import type { MaterialSlotPatternConfig } from '../managers/MaterialSlotPatternConfig';
import type { ViewPreset, VisualizerTheme } from '../types';
import './styles/visualizer.css';

export interface UIControllerOptions {
  container: HTMLElement;
  theme?: VisualizerTheme;
  showMaterialPanel?: boolean;
  showTogglePanel?: boolean;
  showPreviewPanel?: boolean;
  showResetButton?: boolean;
  showFullscreenButton?: boolean;
  showKeybinds?: boolean;
  showViewSnaps?: boolean;
  showFocusLock?: boolean;
}

interface UIState {
  // Material configs
  materialConfigs: MaterialSlotPatternConfig[];
  
  // View presets
  viewPresets: ViewPreset[];
  activeViewPresetId: string | null;
  
  // Background
  hdriOptions: Array<{ id: string; name: string; url: string }>;
  
  // Loading/error states
  isLoading: boolean;
  loadingMessage: string;
  loadingProgress?: number;
  errorMessage: string | null;
  
  // Focus lock
  focusLockActive: boolean;
  
  // Keybinds
  keybindsVisible: boolean;
}

export class UIController {
  private container: HTMLElement;
  private overlay: HTMLElement;
  private options: UIControllerOptions;
  private theme: VisualizerTheme;
  
  // State
  private state: UIState = {
    materialConfigs: [],
    viewPresets: [],
    activeViewPresetId: null,
    hdriOptions: [],
    isLoading: false,
    loadingMessage: '',
    errorMessage: null,
    focusLockActive: false,
    keybindsVisible: false,
  };
  
  // Callbacks
  private onMaterialChange?: (slotId: string, optionId: string) => void;
  private onViewSnap?: (presetId: string) => void;
  private onHdriChange?: (url: string) => void;
  private onSceneryChange?: (visible: boolean) => void;
  private onReset?: () => void;
  private onFullscreen?: () => void;
  private onFocusLock?: () => void;
  
  constructor(options: UIControllerOptions) {
    this.container = options.container;
    this.options = options;
    this.theme = options.theme ?? {
      primaryColor: '#007bff',
      secondaryColor: '#6c757d',
      backgroundColor: '#ffffff',
    };
    
    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.className = 'visualizer-ui-overlay';
    this.overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 10;
    `;
    this.container.appendChild(this.overlay);
  }
  
  /**
   * Initialize UI with configuration
   */
  public init(config: {
    materialSlots?: unknown[];
    toggleElements?: unknown[];
    previewScenes?: unknown[];
    viewPresets?: ViewPreset[];
  }): void {
    this.state.viewPresets = config.viewPresets || [];
    this.render();
  }
  
  /**
   * Create pattern material panel from auto-detected slots
   */
  public createPatternMaterialPanel(patternConfigs: MaterialSlotPatternConfig[]): void {
    this.state.materialConfigs = patternConfigs;
    this.render();
  }
  
  /**
   * Create background panel
   */
  public createBackgroundPanel(config: {
    hdriOptions: Array<{ id: string; name: string; url: string }>;
    onHdriChange: (url: string) => void;
    onSceneryChange: (visible: boolean) => void;
  }): void {
    this.state.hdriOptions = config.hdriOptions;
    this.onHdriChange = config.onHdriChange;
    this.onSceneryChange = config.onSceneryChange;
    this.render();
  }
  
  /**
   * Show loading state
   */
  public showLoading(message?: string, progress?: number): void {
    this.state.isLoading = true;
    this.state.loadingMessage = message || 'Loading...';
    this.state.loadingProgress = progress;
    this.render();
  }
  
  /**
   * Hide loading state
   */
  public hideLoading(): void {
    this.state.isLoading = false;
    this.render();
  }
  
  /**
   * Show error state
   */
  public showError(message: string): void {
    this.state.errorMessage = message;
    this.render();
  }
  
  /**
   * Hide error state
   */
  public hideError(): void {
    this.state.errorMessage = null;
    this.render();
  }
  
  /**
   * Update active view
   */
  public updateActiveView(presetId: string | null): void {
    this.state.activeViewPresetId = presetId;
    this.render();
  }
  
  /**
   * Update focus lock state
   */
  public updateFocusLockState(isActive: boolean): void {
    this.state.focusLockActive = isActive;
    this.render();
  }
  
  /**
   * Set callbacks
   */
  public setOnMaterialChange(callback: (slotId: string, optionId: string) => void): void {
    this.onMaterialChange = callback;
  }
  
  public setOnReset(callback: () => void): void {
    this.onReset = callback;
  }
  
  public setOnFullscreen(callback: () => void): void {
    this.onFullscreen = callback;
  }
  
  public setOnViewSnap(callback: (presetId: string) => void): void {
    this.onViewSnap = callback;
  }
  
  public setOnFocusLock(callback: () => void): void {
    this.onFocusLock = callback;
  }
  
  // Stub methods for compatibility
  public setOnToggleChange(_callback: unknown): void {}
  public setOnPreviewChange(_callback: unknown): void {}
  public updateMaterialSelection(_slotId: string, _optionId: string): void {}
  public updateToggle(_elementId: string, _visible: boolean): void {}
  public updatePreviewSelection(_sceneId: string | null): void {}
  public setTheme(_theme: VisualizerTheme): void {}
  
  /**
   * Render the UI using Preact
   */
  private render(): void {
    const ui = (
      <div style={{ '--primary-color': this.theme.primaryColor }}>
        {/* Material panel */}
        {this.options.showMaterialPanel !== false && this.state.materialConfigs.length > 0 && (
          <MaterialsPanel
            patternConfigs={this.state.materialConfigs}
            onMaterialChange={this.onMaterialChange || (() => {})}
          />
        )}
        
        {/* View controls */}
        {this.options.showViewSnaps && this.state.viewPresets.length > 0 && (
          <ViewPresetsDropdown
            presets={this.state.viewPresets}
            activePresetId={this.state.activeViewPresetId}
            onViewSnap={this.onViewSnap || (() => {})}
          />
        )}
        
        {/* Background panel */}
        {this.state.hdriOptions.length > 0 && (
          <BackgroundPanel
            hdriOptions={this.state.hdriOptions}
            onHdriChange={this.onHdriChange || (() => {})}
            onSceneryChange={this.onSceneryChange || (() => {})}
          />
        )}
        
        {/* Keybinds panel */}
        {this.options.showKeybinds && (
          <KeybindsPanel isVisible={this.state.keybindsVisible} />
        )}
        
        {/* Controls bar */}
        <ControlsBar
          showReset={this.options.showResetButton}
          showFullscreen={this.options.showFullscreenButton}
          showKeybinds={this.options.showKeybinds}
          showFocusLock={this.options.showFocusLock}
          focusLockActive={this.state.focusLockActive}
          onReset={this.onReset}
          onFullscreen={this.onFullscreen}
          onToggleKeybinds={() => {
            this.state.keybindsVisible = !this.state.keybindsVisible;
            this.render();
          }}
          onToggleFocusLock={this.onFocusLock}
        />
        
        {/* Loading overlay */}
        {this.state.isLoading && (
          <LoadingOverlay
            message={this.state.loadingMessage}
            progress={this.state.loadingProgress}
            primaryColor={this.theme.primaryColor}
          />
        )}
        
        {/* Error overlay */}
        {this.state.errorMessage && (
          <ErrorOverlay message={this.state.errorMessage} />
        )}
      </div>
    );
    
    render(ui, this.overlay);
  }
  
  /**
   * Clean up
   */
  public dispose(): void {
    if (this.overlay && this.overlay.parentElement) {
      render(null, this.overlay);
      this.overlay.remove();
    }
  }
}
